import React, { useState, useEffect } from 'react';
import {
  Container
} from 'reactstrap';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/autoplay';
import axios from 'axios';
import api from '../../config.js';

// // Get base URL from environment
// const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000';

const ImageSlider = () => {
  // State management
  const [strategicPartners, setStrategicPartners] = useState([]);
  const [jointInitiatives, setJointInitiatives] = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(true);
  const [initiativesLoading, setInitiativesLoading] = useState(true);
  const [partnersError, setPartnersError] = useState(null);
  const [initiativesError, setInitiativesError] = useState(null);

  // Fetch Partners data
  const fetchPartners = async () => {
    try {
      setPartnersLoading(true);
      setPartnersError(null);
      
      const response = await axios.get('/api/auth/get/all/partner');
      
      if (response.data.isOk) {
        const partnersData = response.data.data.map(partner => ({
          id: partner._id,
          src: partner.logo ? `${api.API_URL}/${partner.logo}` : null,
          alt: partner.name,
          name: partner.name
        })).filter(partner => partner.src); // Only include partners with logos
        
        setStrategicPartners(partnersData);
      } else {
        setPartnersError('Failed to fetch partners data');
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
      setPartnersError('Error loading partners');
      setStrategicPartners([]); // Fallback to empty array
    } finally {
      setPartnersLoading(false);
    }
  };

  // Fetch Initiatives data
  const fetchInitiatives = async () => {
    try {
      setInitiativesLoading(true);
      setInitiativesError(null);
      
      const response = await axios.get(`/api/auth/get/all/initiative`);
      
      if (response.data.isOk) {
        const initiativesData = response.data.data.map(initiative => ({
          id: initiative._id,
          src: initiative.logo ? `${api.API_URL}/${initiative.logo}` : null,
          alt: initiative.name,
          name: initiative.name
        })).filter(initiative => initiative.src); // Only include initiatives with logos
        
        setJointInitiatives(initiativesData);
      } else {
        setInitiativesError('Failed to fetch initiatives data');
      }
    } catch (error) {
      console.error('Error fetching initiatives:', error);
      setInitiativesError('Error loading initiatives');
      setJointInitiatives([]); // Fallback to empty array
    } finally {
      setInitiativesLoading(false);
    }
  };

  // Helper function to ensure minimum slides for smooth looping
  const ensureMinimumSlides = (items, minSlides = 6) => {
    if (items.length === 0) return [];
    if (items.length >= minSlides) return items;
    
    const repeated = [];
    while (repeated.length < minSlides) {
      repeated.push(...items);
    }
    return repeated.slice(0, minSlides);
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchPartners();
    fetchInitiatives();
  }, []);

  return (
    <>
      {/* Strategic Partners Section */}
      <section className="partners-section pb-0">
        <Container className="text-center partner-bottom">
          <h5 className="partners-title mb-3 mb-md-5">Thanks to our leading Strategic Partners</h5>
          
          {partnersLoading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading partners...</span>
              </div>
            </div>
          ) : partnersError ? (
            <div className="alert alert-warning" role="alert">
              {partnersError}
            </div>
          ) : strategicPartners.length === 0 ? (
            <div className="alert alert-info" role="alert">
              No strategic partners available
            </div>
          ) : (
            <Swiper
              modules={[Autoplay]}
              speed={1500}
              slidesPerView={5}
              spaceBetween={30}
              loop={true}
              autoplay={{
                delay: 2000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }}
              breakpoints={{
                1191: { slidesPerView: 5 },
                992: { slidesPerView: 4 },
                768: { slidesPerView: 3 },
                575: { slidesPerView: 2 },
                320: { slidesPerView: 2 },
              }}
              style={{ padding: "20px 0" }}
              className="partners-swiper"
            >
              {ensureMinimumSlides(strategicPartners).map((partner, index) => (
                <SwiperSlide key={`${partner.id}-${index}`}>
                  <div className="clients-logo">
                    <img
                      className="partner-logo"
                      src={partner.src}
                      alt={partner.alt}
                      width={150}
                      height={150}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        console.error('Failed to load partner image:', partner.src);
                      }}
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          )}
        </Container>
      </section>

      {/* Joint Initiatives Section */}
      <section className="partners-section">
        <Container className="text-center">
          <h5 className="partners-title mb-3 mb-md-5">Joint Initiatives</h5>
          
          {initiativesLoading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading initiatives...</span>
              </div>
            </div>
          ) : initiativesError ? (
            <div className="alert alert-warning" role="alert">
              {initiativesError}
            </div>
          ) : jointInitiatives.length === 0 ? (
            <div className="alert alert-info" role="alert">
              No joint initiatives available
            </div>
          ) : (
            <Swiper
              modules={[Autoplay]}
              speed={1500}
              slidesPerView={5}
              spaceBetween={30}
              loop={true}
              autoplay={{
                delay: 2000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }}
              breakpoints={{
                1191: { slidesPerView: 5 },
                992: { slidesPerView: 4 },
                768: { slidesPerView: 3 },
                575: { slidesPerView: 2 },
                320: { slidesPerView: 2 },
              }}
              style={{ padding: "20px 0" }}
              className="initiatives-swiper"
            >
              {ensureMinimumSlides(jointInitiatives).map((initiative, index) => (
                <SwiperSlide key={`${initiative.id}-${index}`}>
                  <div className="clients-logo">
                    <img
                      className="partner-logo"
                      src={initiative.src}
                      alt={initiative.alt}
                      width={150}
                      height={150}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        console.error('Failed to load initiative image:', initiative.src);
                      }}
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          )}
        </Container>
      </section>
    </>
  );
};

export default ImageSlider;
